// File handling utilities for chat interface

export type FileType = 'image' | 'audio' | 'document';

export interface FileValidationResult {
  valid: boolean;
  error?: string;
  fileType?: FileType;
  mimeType?: string;
}

// Supported file types and their MIME types
const IMAGE_TYPES = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
  'image/bmp': ['.bmp'],
};

const AUDIO_TYPES = {
  'audio/mpeg': ['.mp3', '.mpeg', '.mpga'],
  'audio/mp4': ['.mp4', '.m4a'],
  'audio/wav': ['.wav'],
  'audio/webm': ['.webm'],
};

const DOCUMENT_TYPES = {
  'text/plain': ['.txt'],
  'text/markdown': ['.md'],
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'text/csv': ['.csv'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
};

// File size limits (in bytes)
const MAX_IMAGE_SIZE = 20 * 1024 * 1024; // 20MB
const MAX_AUDIO_SIZE = 25 * 1024 * 1024; // 25MB
const MAX_DOCUMENT_SIZE = 25 * 1024 * 1024; // 25MB

/**
 * Get file extension from filename
 */
function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  return parts.length > 1 ? '.' + parts[parts.length - 1].toLowerCase() : '';
}

/**
 * Detect file type from MIME type or extension
 */
export function detectFileType(file: File): FileType | null {
  const extension = getFileExtension(file.name);
  const mimeType = file.type.toLowerCase();

  // Check images
  for (const [mime, exts] of Object.entries(IMAGE_TYPES)) {
    if (mimeType === mime || exts.includes(extension)) {
      return 'image';
    }
  }

  // Check audio
  for (const [mime, exts] of Object.entries(AUDIO_TYPES)) {
    if (mimeType === mime || exts.includes(extension)) {
      return 'audio';
    }
  }

  // Check documents
  for (const [mime, exts] of Object.entries(DOCUMENT_TYPES)) {
    if (mimeType === mime || exts.includes(extension)) {
      return 'document';
    }
  }

  return null;
}

/**
 * Get MIME type for a file
 */
export function getMimeType(file: File): string {
  if (file.type) {
    return file.type;
  }

  const extension = getFileExtension(file.name);
  const fileType = detectFileType(file);

  if (fileType === 'image') {
    for (const [mime, exts] of Object.entries(IMAGE_TYPES)) {
      if (exts.includes(extension)) {
        return mime;
      }
    }
    return 'image/jpeg'; // Default
  }

  if (fileType === 'audio') {
    for (const [mime, exts] of Object.entries(AUDIO_TYPES)) {
      if (exts.includes(extension)) {
        return mime;
      }
    }
    return 'audio/mpeg'; // Default
  }

  if (fileType === 'document') {
    for (const [mime, exts] of Object.entries(DOCUMENT_TYPES)) {
      if (exts.includes(extension)) {
        return mime;
      }
    }
    return 'text/plain'; // Default
  }

  return 'application/octet-stream';
}

/**
 * Validate file before upload
 */
export function validateFile(file: File): FileValidationResult {
  const fileType = detectFileType(file);

  if (!fileType) {
    return {
      valid: false,
      error: `Unsupported file type. Supported: images (${Object.values(IMAGE_TYPES).flat().join(', ')}), audio (${Object.values(AUDIO_TYPES).flat().join(', ')}), documents (${Object.values(DOCUMENT_TYPES).flat().join(', ')})`,
    };
  }

  // Check file size
  let maxSize: number;
  switch (fileType) {
    case 'image':
      maxSize = MAX_IMAGE_SIZE;
      break;
    case 'audio':
      maxSize = MAX_AUDIO_SIZE;
      break;
    case 'document':
      maxSize = MAX_DOCUMENT_SIZE;
      break;
    default:
      maxSize = MAX_DOCUMENT_SIZE;
  }

  if (file.size > maxSize) {
    const maxSizeMB = maxSize / (1024 * 1024);
    return {
      valid: false,
      error: `File size exceeds maximum of ${maxSizeMB}MB`,
    };
  }

  return {
    valid: true,
    fileType,
    mimeType: getMimeType(file),
  };
}

/**
 * Convert file to base64 string
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:image/jpeg;base64,")
      const base64 = result.split(',')[1] || result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Detect if device is mobile
 */
export function isMobileDevice(): boolean {
  // Check user agent
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
  
  if (mobileRegex.test(userAgent.toLowerCase())) {
    return true;
  }

  // Check screen width (fallback)
  if (window.innerWidth <= 768) {
    return true;
  }

  // Check for touch support
  if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
    return true;
  }

  return false;
}

/**
 * Check if camera is available
 */
export function isCameraAvailable(): Promise<boolean> {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    return Promise.resolve(false);
  }

  return navigator.mediaDevices
    .enumerateDevices()
    .then((devices) => {
      return devices.some((device) => device.kind === 'videoinput');
    })
    .catch(() => false);
}
